import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export function CreateAutomationDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);

  const handleOpenChange = (nextOpen: boolean) => {
    if (isCreating) return;
    onOpenChange(nextOpen);
    if (!nextOpen) {
      setIsCreating(false);
    }
  };

  const createAndOpen = async ({
    body,
    tab,
  }: {
    body: Record<string, unknown>;
    tab?: "template";
  }) => {
    setIsCreating(true);
    try {
      const response = await fetch("/api/automations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (!response.ok || !data.automation?._id) {
        throw new Error(data.error || "Failed to create flow");
      }

      onOpenChange(false);
      setIsCreating(false);
      const suffix = tab ? `?tab=${tab}` : "";
      navigate(`/automations/${data.automation._id}/edit${suffix}`);
    } catch (error) {
      console.error("Failed to create automation:", error);
      alert(
        error instanceof Error
          ? error.message
          : "An error occurred while creating flow"
      );
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Templates</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <button
            type="button"
            data-testid="automations-create-scratch"
            disabled={isCreating}
            onClick={() => void createAndOpen({ body: { name: "Untitled" } })}
            className="w-full rounded-lg border p-4 text-left transition hover:border-primary disabled:opacity-50"
          >
            <div className="flex items-center gap-2 font-medium">
              Create from Scratch
              {isCreating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Start with an empty flow and add your own steps.
            </p>
          </button>
          <button
            type="button"
            data-testid="automations-create-link-in-bio"
            disabled={isCreating}
            onClick={() =>
              void createAndOpen({
                body: { name: "Link in Bio", linkInBio: {} },
                tab: "template",
              })
            }
            className="w-full rounded-lg border p-4 text-left transition hover:border-primary disabled:opacity-50"
          >
            <div className="flex items-center gap-2 font-medium">
              Link in Bio
              {isCreating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              A profile page with socials, links, and a fan capture form.
            </p>
          </button>
          <button
            type="button"
            data-testid="automations-create-sell-a-product"
            disabled={isCreating}
            onClick={() =>
              void createAndOpen({
                body: { name: "Sell Merch", sellAProduct: {} },
                tab: "template",
              })
            }
            className="w-full rounded-lg border p-4 text-left transition hover:border-primary disabled:opacity-50"
          >
            <div className="flex items-center gap-2 font-medium">
              Sell Merch
              {isCreating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              A product page with a countdown, description, and buy bar.
            </p>
          </button>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isCreating}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
